import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { useBooking } from "@/lib/booking-context";
import { BOOKING } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const variants = {
  enter: { opacity: 0, y: 30 },
  center: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -30 },
};

const OptionButton = ({ label, active, onClick, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`w-full text-left px-5 sm:px-7 py-5 border rounded-sm transition-all duration-300 font-medium text-[15px] md:text-base ${
      active
        ? "border-champagne bg-champagne/10 text-charcoal"
        : "border-stone/70 text-charcoal/80 hover:border-charcoal hover:bg-charcoal/[0.02]"
    }`}
  >
    {label}
  </button>
);

const StepShell = ({ label, title, children }) => (
  <div className="w-full max-w-2xl mx-auto">
    <p className="overline text-champagne mb-5">{label}</p>
    <h2 className="font-serif text-3xl md:text-5xl font-light text-charcoal leading-[1.1] mb-10">{title}</h2>
    {children}
  </div>
);

export const BookingFlow = () => {
  const { open, closeBooking } = useBooking();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    frequency: "",
    home_size: "",
    bedrooms: "",
    bathrooms: "",
    focus_areas: [],
    has_pets: "",
    preferred_date: null,
    name: "",
    phone: "",
    email: "",
    address: "",
    company: "",
  });

  const TOTAL_QUESTIONS = 8;
  const reset = () => { setStep(0); setTouched({}); setForm({ frequency: "", home_size: "", bedrooms: "", bathrooms: "", focus_areas: [], has_pets: "", preferred_date: null, name: "", phone: "", email: "", address: "", company: "" }); };
  const handleClose = () => { closeBooking(); setTimeout(reset, 400); };

  const next = () => setStep((s) => s + 1);
  const back = () => setStep((s) => Math.max(0, s - 1));
  const pick = (key, val) => { setForm((f) => ({ ...f, [key]: val })); setTimeout(next, 180); };
  const toggleArea = (a) =>
    setForm((f) => ({
      ...f,
      focus_areas: f.focus_areas.includes(a) ? f.focus_areas.filter((x) => x !== a) : [...f.focus_areas, a],
    }));

  const [touched, setTouched] = useState({});

  const formatPhone = (val) => {
    const d = val.replace(/\D/g, "").slice(0, 10);
    if (d.length < 4) return d;
    if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  };
  const phoneDigits = form.phone.replace(/\D/g, "");
  const phoneValid = phoneDigits.length === 10;
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim());
  const infoValid = form.name.trim() && phoneValid && emailValid && form.address.trim();

  const errors = {
    name: !form.name.trim() ? "Please enter your name." : "",
    phone: !phoneDigits ? "Please enter your phone number." : !phoneValid ? "Enter a complete 10-digit phone number." : "",
    email: !form.email.trim() ? "Please enter your email." : !emailValid ? "Enter a valid email address." : "",
    address: !form.address.trim() ? "Please enter your home address." : "",
  };
  const markTouched = (k) => setTouched((t) => ({ ...t, [k]: true }));

  const submit = async () => {
    if (!infoValid) {
      setTouched({ name: true, phone: true, email: true, address: true });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, {
        ...form,
        preferred_date: form.preferred_date ? format(form.preferred_date, "MMMM d, yyyy") : "Flexible",
      });
      setStep(9);
    } catch (e) {
      console.error(e);
      alert("Something went wrong. Please try again or call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const progress = step === 0 ? 0 : Math.min(step, TOTAL_QUESTIONS) / TOTAL_QUESTIONS;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          className="fixed inset-0 z-[100] bg-warmwhite flex flex-col"
          data-testid="booking-flow"
        >
          {/* Top bar */}
          <div className="shrink-0 px-6 md:px-12 h-20 flex items-center justify-between border-b border-stone/50">
            <span className="font-serif text-lg text-charcoal">The <span className="italic">Standard</span></span>
            <button onClick={handleClose} className="text-charcoal/60 hover:text-charcoal text-3xl leading-none" data-testid="booking-close" aria-label="Close">×</button>
          </div>

          {/* Progress */}
          {step > 0 && step < 9 && (
            <div className="shrink-0 h-px bg-stone/40">
              <motion.div className="h-px bg-champagne" animate={{ width: `${progress * 100}%` }} transition={{ ease: [0.16, 1, 0.3, 1], duration: 0.6 }} />
            </div>
          )}

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 md:px-12 py-12 md:py-20 flex items-start md:items-center">
            <div className="w-full">
              <AnimatePresence mode="wait">
                <motion.div key={step} variants={variants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}>

                  {step === 0 && (
                    <div className="w-full max-w-2xl mx-auto text-center md:text-left">
                      <p className="overline text-champagne mb-6">Your Personalized Plan</p>
                      <h2 className="font-serif text-4xl md:text-6xl font-light text-charcoal leading-[1.08]">
                        Let's create your personalized cleaning plan.
                      </h2>
                      <p className="mt-6 text-charcoal/60 leading-relaxed max-w-lg">
                        A few refined questions — about 60 seconds. No paperwork, just a beautifully simple planning experience.
                      </p>
                      <button onClick={next} className="mt-10 px-9 py-4 bg-charcoal text-warmwhite rounded-full text-sm font-semibold transition-all duration-300 hover:bg-champagne hover:scale-[1.03]" data-testid="booking-start-btn">
                        Get Started
                      </button>
                    </div>
                  )}

                  {step === 1 && (
                    <StepShell label="Step 1 of 8" title="How often would you like service?">
                      <div className="grid gap-4">
                        {BOOKING.frequency.map((o) => (
                          <OptionButton key={o} label={o} active={form.frequency === o} onClick={() => pick("frequency", o)} testid={`freq-${o}`} />
                        ))}
                      </div>
                    </StepShell>
                  )}

                  {step === 2 && (
                    <StepShell label="Step 2 of 8" title="Approximately how large is your home?">
                      <div className="grid gap-4">
                        {BOOKING.homeSize.map((o) => (
                          <OptionButton key={o} label={o} active={form.home_size === o} onClick={() => pick("home_size", o)} testid={`size-${o}`} />
                        ))}
                      </div>
                    </StepShell>
                  )}

                  {step === 3 && (
                    <StepShell label="Step 3 of 8" title="How many bedrooms?">
                      <div className="grid grid-cols-3 gap-4">
                        {BOOKING.rooms.map((o) => (
                          <OptionButton key={o} label={o} active={form.bedrooms === o} onClick={() => pick("bedrooms", o)} testid={`bed-${o}`} />
                        ))}
                      </div>
                    </StepShell>
                  )}

                  {step === 4 && (
                    <StepShell label="Step 4 of 8" title="How many bathrooms?">
                      <div className="grid grid-cols-3 gap-4">
                        {BOOKING.rooms.map((o) => (
                          <OptionButton key={o} label={o} active={form.bathrooms === o} onClick={() => pick("bathrooms", o)} testid={`bath-${o}`} />
                        ))}
                      </div>
                    </StepShell>
                  )}

                  {step === 5 && (
                    <StepShell label="Step 5 of 8" title="Areas needing additional attention">
                      <div className="grid sm:grid-cols-2 gap-4">
                        {BOOKING.focusAreas.map((o) => (
                          <OptionButton key={o} label={o} active={form.focus_areas.includes(o)} onClick={() => toggleArea(o)} testid={`area-${o}`} />
                        ))}
                      </div>
                      <button onClick={next} className="mt-10 px-9 py-4 bg-charcoal text-warmwhite rounded-full text-sm font-semibold transition-all duration-300 hover:bg-champagne hover:scale-[1.03]" data-testid="area-continue-btn">
                        Continue
                      </button>
                    </StepShell>
                  )}

                  {step === 6 && (
                    <StepShell label="Step 6 of 8" title="Do you have pets?">
                      <div className="grid grid-cols-2 gap-4 max-w-md">
                        {BOOKING.pets.map((o) => (
                          <OptionButton key={o} label={o} active={form.has_pets === o} onClick={() => pick("has_pets", o)} testid={`pets-${o}`} />
                        ))}
                      </div>
                    </StepShell>
                  )}

                  {step === 7 && (
                    <StepShell label="Step 7 of 8" title="Preferred cleaning date">
                      <div className="inline-block max-w-full overflow-x-auto border border-stone/70 rounded-sm p-3 sm:p-4 bg-warmwhite" data-testid="booking-calendar">
                        <Calendar
                          mode="single"
                          selected={form.preferred_date}
                          onSelect={(d) => setForm((f) => ({ ...f, preferred_date: d }))}
                          disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                        />
                      </div>
                      <div className="mt-8">
                        <button onClick={next} className="px-9 py-4 bg-charcoal text-warmwhite rounded-full text-sm font-semibold transition-all duration-300 hover:bg-champagne hover:scale-[1.03]" data-testid="date-continue-btn">
                          {form.preferred_date ? "Continue" : "I'm flexible — Continue"}
                        </button>
                      </div>
                    </StepShell>
                  )}

                  {step === 8 && (
                    <StepShell label="Step 8 of 8" title="Your information">
                      <div className="grid gap-5 max-w-xl">
                        {[
                          { k: "name", label: "Full Name", type: "text", ph: "Jane Anderson", mode: undefined },
                          { k: "phone", label: "Phone", type: "tel", ph: "(470) 424-6239", mode: "tel" },
                          { k: "email", label: "Email", type: "email", ph: "you@example.com", mode: "email" },
                          { k: "address", label: "Home Address", type: "text", ph: "123 Estate Drive, City, State", mode: undefined },
                        ].map((fld) => {
                          const showErr = touched[fld.k] && errors[fld.k];
                          return (
                          <div key={fld.k}>
                            <label className="block text-charcoal/60 text-sm mb-2">{fld.label}</label>
                            <input
                              type={fld.type}
                              inputMode={fld.mode}
                              value={form[fld.k]}
                              placeholder={fld.ph}
                              onBlur={() => markTouched(fld.k)}
                              onChange={(e) => {
                                const v = fld.k === "phone" ? formatPhone(e.target.value) : e.target.value;
                                setForm((f) => ({ ...f, [fld.k]: v }));
                              }}
                              aria-invalid={!!showErr}
                              className={`w-full px-5 py-4 border rounded-sm bg-warmwhite text-charcoal focus:outline-none transition-colors ${
                                showErr ? "border-red-500 focus:border-red-500" : "border-stone/70 focus:border-champagne"
                              }`}
                              data-testid={`info-${fld.k}`}
                            />
                            {showErr && (
                              <p className="text-red-600 text-xs mt-1.5" data-testid={`error-${fld.k}`}>{errors[fld.k]}</p>
                            )}
                          </div>
                          );
                        })}
                        <input
                          type="text"
                          tabIndex={-1}
                          autoComplete="off"
                          aria-hidden="true"
                          value={form.company}
                          onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))}
                          className="absolute left-[-9999px] w-px h-px opacity-0"
                          data-testid="hp-company"
                        />
                        <button
                          onClick={submit}
                          disabled={submitting}
                          className="mt-4 px-9 py-4 bg-charcoal text-warmwhite rounded-full text-sm font-semibold transition-all duration-300 hover:bg-champagne hover:scale-[1.03] disabled:opacity-40 disabled:hover:scale-100 disabled:cursor-not-allowed"
                          data-testid="booking-submit-btn"
                        >
                          {submitting ? "Sending…" : "Complete My Plan"}
                        </button>
                      </div>
                    </StepShell>
                  )}

                  {step === 9 && (
                    <div className="w-full max-w-2xl mx-auto text-center">
                      <motion.div
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: 0.2, type: "spring", stiffness: 200, damping: 18 }}
                        className="w-16 h-16 rounded-full bg-forest mx-auto mb-10 flex items-center justify-center"
                      >
                        <span className="text-warmwhite text-2xl">✓</span>
                      </motion.div>
                      <p className="overline text-champagne mb-5">Request Received</p>
                      <h2 className="font-serif text-4xl md:text-6xl font-light text-charcoal leading-[1.08]">Thank you.</h2>
                      <p className="mt-6 text-charcoal/65 leading-relaxed max-w-lg mx-auto">
                        We've received your request. One of our specialists will contact you within 5–10 minutes during business hours to discuss your personalized cleaning plan and provide your customized quote.
                      </p>
                      <button onClick={handleClose} className="mt-10 px-9 py-4 border border-charcoal text-charcoal rounded-full text-sm font-semibold transition-all duration-300 hover:bg-charcoal hover:text-warmwhite" data-testid="booking-done-btn">
                        Return to Site
                      </button>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          {/* Back button */}
          {step > 0 && step < 9 && (
            <div className="shrink-0 px-6 md:px-12 py-6 border-t border-stone/50">
              <button onClick={back} className="text-charcoal/50 hover:text-charcoal text-sm link-underline" data-testid="booking-back-btn">← Back</button>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};
